import React, { useEffect, useState } from "react";
import {
  Card, CardBody, Row, Col, Input, Button, Table, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { textCapitalize } from "../../Helpers/Utils";
import JsBarcode from "jsbarcode";
import Select from "react-select";

const ProductList = () => {

  const dispatch = useDispatch(), navigate = useNavigate();
  const user = useSelector(s => s.customer.customer);
  const loading = useSelector(state => state.loading.loading);
  const [priceTagOpen, setPriceTagOpen] = useState(false);
  const [priceTagData, setPriceTagData] = useState(null);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  /* VARIANT MODAL */
  const [variantOpen, setVariantOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);

  const [variant, setVariant] = useState({
    id: null,
    name: "",
    price: "",
    purchase_price: "0",
    discount_type: { value: "percentage", label: "Percentage" },
    discount_value: "0",
    gst_rate: "0",
    quantity: "1",
    low_stock_limit: "0"
  });

  const discountTypes = [
    { value: "flat", label: "Flat" },
    { value: "percentage", label: "Percentage" }
  ];

  const limit = 10;

  const renderBarcode = (id, value) => {
    if (!value) return;
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        JsBarcode(el, value, {
          format: "CODE128",
          width: 2,
          height: 60,
          displayValue: true,
          fontSize: 14
        });
      }
    }, 50);
  };


  /* LOAD SERVICES */
  const load = async () => {
    dispatch(loadingTrue());
    try {
      const res = await Handler({
        method: "get",
        url: `/products/search.php?outlet_id=${user?.outlet_id}&q=${search}`
      });
      res?.success ? setRows(res.data || []) : toast.error(res?.message);
    } catch {
      toast.error("Network error");
    }
    dispatch(loadingFalse());
  };

  useEffect(() => { load(); }, [search]);

  /* PAGINATION */
  const total = rows.length;
  const start = (page - 1) * limit;
  const pagedRows = rows.slice(start, start + limit);

  /* STOCK COLOR (For services, high is always good) */
  const stockColor = (qty, low = 0) => {
    if (qty <= low) return "danger";
    return "success";
  };

  /* SAVE PACKAGE (VARIANT) */
  const saveVariant = async () => {

    if (!variant.name.trim()) return toast.error("Package name required");
    if (!variant.price) return toast.error("Price required");

    dispatch(loadingTrue());

    const payload = {
      product_id: currentProduct.product_id,
      variant_id: variant.id,
      name: variant.name,
      price: Number(variant.price),
      purchase_price: Number(variant.purchase_price || 0),
      discount_type: variant.discount_type?.value || "percentage",
      discount_value: Number(variant.discount_value || 0),
      gst_rate: Number(variant.gst_rate || 0),
      quantity: Number(variant.quantity || 1),
      low_stock_limit: Number(variant.low_stock_limit || 0)
    };

    try {
      const res = await Handler({
        method: "post",
        url: variant.id
          ? "/products/variant/update.php"
          : "/products/variant/add.php",
        data: payload
      });

      if (res?.success) {
        toast.success("Package saved successfully");
        setVariantOpen(false);
        load();
      } else toast.error(res?.message);
    } catch {
      toast.error("Network error");
    }

    dispatch(loadingFalse());
  };

  /* PRICE TAG LOGIC (Keep as is for barcode needs) */
  const downloadPriceTag = async (productId) => {
    dispatch(loadingTrue());
    try {
      const res = await Handler({
        method: "get",
        url: `/products/price_tag_view.php?product_id=${productId}`
      });
      if (!res?.success) {
        toast.error("Tag not available");
        return;
      }
      setPriceTagData(res.data);
      setPriceTagOpen(true);
    } catch {
      toast.error("Network error");
    }
    dispatch(loadingFalse());
  };

  useEffect(() => {
    if (!priceTagOpen || !priceTagData) return;
    if (priceTagData?.product?.price_tag?.barcode) {
      renderBarcode("product-barcode", priceTagData.product.price_tag.barcode);
    }
    priceTagData?.variants?.forEach(v => {
      renderBarcode(`variant-barcode-${v.variant_id}`, v.price_tag.barcode);
    });
  }, [priceTagOpen, priceTagData]);


  return (
    <>
      <Row className="mb-3">
        <Col><h5 className="fw-bold">Digital Marketing - Services List</h5></Col>
      </Row>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <Input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search Service / SKU / Category..."
          />
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardBody>
          <Table responsive className="modern-table align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Service ID</th>
                <th>Service Details</th>
                <th>Config / Meta</th>
                <th className="text-end">Qty/Stock</th>
                <th className="text-end">Base Price</th>
                <th className="text-end">Packages</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>

              {pagedRows.map((p, i) => (
                <React.Fragment key={i}>
                  <tr>
                    <td>{start + i + 1}</td>
                    <td>{p.product_id}</td>
                    <td>
                      <b className="text-primary">{p.name}</b>
                      {p.discount?.value > 0 &&
                        <div>
                          <Badge color="soft-danger" className="mt-1 text-danger">
                            {p.discount.type === "percentage"
                              ? `${p.discount.value}% OFF`
                              : `₹${p.discount.value} OFF`}
                          </Badge>
                        </div>}
                    </td>

                    <td className="small text-muted">
                      {p.meta?.sku && <div>SKU: {p.meta.sku}</div>}
                      {p.meta?.size && <div>Unit: {p.meta.size}</div>}
                      {p.category_name && <div className="text-dark fw-medium">{p.category_name}</div>}
                    </td>

                    <td className="text-end">
                      <Badge color={stockColor(p.quantity, p.low_stock_limit)}>
                        {p.quantity}
                      </Badge>
                    </td>

                    <td className="text-end fw-bold text-dark">₹ {Number(p.price).toLocaleString()}</td>

                    <td className="text-end">
                      <Badge pill color="info">{p.variants?.length || 0} Packs</Badge>
                    </td>

                    <td className="text-end">
                      <Button disabled={loading} size="sm" color="light" className="me-1"
                        onClick={() => navigate(`/products-edit/${p.product_id}`)} title="Edit Service">
                        <i className="bx bx-edit"></i>
                      </Button>

                      <Button disabled={loading} size="sm" color="light" className="me-1"
                        onClick={() => downloadPriceTag(p.product_id)} title="View Barcode/Tag">
                        <i className="bx bx-purchase-tag"></i>
                      </Button>

                      <Button disabled={loading} size="sm" color="light" className="me-1"
                        onClick={() => navigate(`/products-view/${p.product_id}`)} title="View Details">
                        <i className="bx bx-show"></i>
                      </Button>

                      <Button disabled={loading} size="sm" color="primary" outline
                        onClick={() => {
                          setCurrentProduct(p);
                          setVariant({
                            id: null, name: "", price: "", purchase_price: "0",
                            discount_type: discountTypes[1], discount_value: "0",
                            gst_rate: p.gst_rate || "0", quantity: "1", low_stock_limit: "0"
                          });
                          setVariantOpen(true);
                        }} title="Add New Package">
                        <i className="bx bx-plus"></i>
                      </Button>
                    </td>
                  </tr>

                  {/* PACKAGES (VARIANTS) ROWS */}
                  { p?.variants?.map((v, vi) => (
                    <tr key={vi} className="table-light">
                      <td></td>
                      <td className="small text-muted text-end">Package:</td>
                      <td className="ps-4">
                        <span className="text-muted">↳</span> <span className="fw-medium">{v.name}</span>
                      </td>
                      <td className="small text-muted">
                        GST {v.gst_rate}% | Disc {v.discount?.value || 0}
                      </td>
                      <td className="text-end">
                        <small className="text-muted">{v.quantity}</small>
                      </td>
                      <td className="text-end text-primary">₹ {Number(v.price).toLocaleString()}</td>
                      <td></td>
                      <td className="text-end">
                        <Button
                          disabled={loading}
                          size="sm" color="white" className="border-0 shadow-none"
                          onClick={() => {
                            setCurrentProduct(p);
                            setVariant({
                              id: v.variant_id,
                              name: v.name,
                              price: v.price,
                              purchase_price: v.purchase_price,
                              discount_type: discountTypes.find(d => d.value === v.discount?.type) || discountTypes[1],
                              discount_value: v.discount?.value,
                              gst_rate: v.gst_rate,
                              quantity: v.quantity,
                              low_stock_limit: v.low_stock_limit
                            });
                            setVariantOpen(true);
                          }}>
                          <i className="bx bx-edit-alt text-muted"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}

                </React.Fragment>
              ))}

            </tbody>
          </Table>

          <Pagination
            page={page}
            total={total}
            limit={limit}
            onPrev={() => setPage(p => Math.max(1, p - 1))}
            onNext={() => setPage(p => p + 1)}
          />
        </CardBody>
      </Card>

      {/* PACKAGE (VARIANT) MODAL */}
      <Modal isOpen={variantOpen} toggle={() => setVariantOpen(false)} centered size="lg">
        <ModalHeader toggle={() => setVariantOpen(false)}>
          Manage Package – {textCapitalize(currentProduct?.name)}
        </ModalHeader>

        <ModalBody>
          <Row className="g-3">
            <Col md={6}>
                <label className="small fw-bold">Package Name</label>
                <Input placeholder="e.g. 1 Year Maintenance" value={variant.name}
                  onChange={e => setVariant({ ...variant, name: e.target.value })} />
            </Col>
            <Col md={6}>
                <label className="small fw-bold">Package Selling Price</label>
                <Input type="number" placeholder="0.00" value={variant.price}
                  onChange={e => setVariant({ ...variant, price: e.target.value })} />
            </Col>
            <Col md={4}>
                <label className="small fw-bold">Base Cost (Optional)</label>
                <Input type="number" value={variant.purchase_price}
                  onChange={e => setVariant({ ...variant, purchase_price: e.target.value })} />
            </Col>
            <Col md={4}>
                <label className="small fw-bold">Discount Type</label>
                <Select options={discountTypes} value={variant.discount_type}
                  onChange={v => setVariant({ ...variant, discount_type: v })} />
            </Col>
            <Col md={4}>
                <label className="small fw-bold">Discount Value</label>
                <Input type="number" value={variant.discount_value}
                  onChange={e => setVariant({ ...variant, discount_value: e.target.value })} />
            </Col>
            <Col md={4}>
                <label className="small fw-bold">GST %</label>
                <Input type="number" value={variant.gst_rate}
                  onChange={e => setVariant({ ...variant, gst_rate: e.target.value })} />
            </Col>
            <Col md={4}>
                <label className="small fw-bold">Quantity/Slots</label>
                <Input type="number" value={variant.quantity}
                  onChange={e => setVariant({ ...variant, quantity: e.target.value })} />
            </Col>
            <Col md={4}>
                <label className="small fw-bold">Alert Limit</label>
                <Input type="number" value={variant.low_stock_limit}
                  onChange={e => setVariant({ ...variant, low_stock_limit: e.target.value })} />
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button disabled={loading} color="light" onClick={() => setVariantOpen(false)}>Cancel</Button>
          <Button disabled={loading} color="primary" onClick={saveVariant}>Save Package</Button>
        </ModalFooter>
      </Modal>

      {/* PRICE TAG MODAL (Kept for Barcode) */}
      <Modal isOpen={priceTagOpen} toggle={() => setPriceTagOpen(false)} centered size="md">
        <ModalHeader toggle={() => setPriceTagOpen(false)}>Service ID/Barcode Preview</ModalHeader>
        <ModalBody className="text-capitalize">
          {priceTagData?.product && (
            <div className="price-tag-card mb-3 border p-3 text-center">
              <div className="fw-bold">{priceTagData.product.name}</div>
              <div className="small text-muted">SKU: {priceTagData.product.sku}</div>
              <div className="h5 my-2">{priceTagData.product.price_tag.final_price}</div>
              <svg id="product-barcode" className="mt-2"></svg>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setPriceTagOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      <div id="print-root" style={{ display: "none" }}></div>
    </>
  );
};

export default ProductList;